// Epoch 32: Demo Request Modal with Calendly Integration
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InlineWidget } from 'react-calendly';
import { supabase } from '../../supabase/client';
import useGoogleAnalytics from '../../hooks/useGoogleAnalytics';

interface DemoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  source: string; // Which page triggered the modal
  buttonId?: string; // Specific button identifier
}

const DemoRequestModal: React.FC<DemoRequestModalProps> = ({
  isOpen,
  onClose,
  source,
  buttonId = 'unknown'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCalendly, setShowCalendly] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  const modalRef = useRef<HTMLDivElement>(null);
  const ga = useGoogleAnalytics();

  // Define handleClose first, before any useEffect that uses it
  const handleClose = useCallback(() => {
    if (!showCalendly && hasInteracted) {
      ga.trackFormAbandonment('demo', Object.keys(formData).find(key => formData[key as keyof typeof formData]) || 'none');
    }
    onClose();
    // Reset form state
    setFormData({ name: '', email: '', company: '', phone: '' });
    setError(null);
    setShowCalendly(false);
  }, [showCalendly, hasInteracted, formData, ga, onClose]);

  // Track modal open
  useEffect(() => {
    if (isOpen) {
      ga.trackModalOpen('demo', buttonId);
      setHasInteracted(false); // Reset interaction tracking
    }
  }, [isOpen, buttonId, ga]);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, handleClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Track first interaction
    if (!hasInteracted) {
      ga.trackFormStart('demo');
      setHasInteracted(true);
    }
    
    // Track field interaction
    ga.trackFieldInteraction(name, 'demo');
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Please enter your name');
      ga.trackFormError('demo', 'Name required');
      return;
    }
    
    if (!formData.email.trim()) {
      setError('Please enter your email');
      ga.trackFormError('demo', 'Email required');
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      ga.trackFormError('demo', 'Invalid email');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Track submission attempt
    const hasOptionalFields = Boolean(formData.company || formData.phone);
    ga.trackFormSubmitAttempt('demo', hasOptionalFields);
    
    try {
      // Get UTM parameters and GA IDs
      const utmParams = ga.getUTMParams();
      const gaClientId = ga.getGAClientId();
      const gaSessionId = ga.getGASessionId();
      
      // Submit to Edge Function
      const { error: submitError } = await supabase.functions.invoke('demo-request-handler', {
        body: {
          ...formData,
          request_type: 'demo',
          source_page: source,
          source_button: buttonId,
          ...utmParams,
          ga_client_id: gaClientId,
          ga_session_id: gaSessionId
        }
      });
      
      if (submitError) {
        throw submitError;
      }
      
      // Track success
      ga.trackFormSubmitSuccess('demo', formData.company, buttonId);
      
      // Show Calendly
      setShowCalendly(true);
      ga.trackCalendlyView('demo');
      
    } catch (err) {
      console.error('Error submitting demo request:', err);
      setError('Something went wrong. Please try again or email us directly.');
      ga.trackFormError('demo', 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white shadow-xl transition-all"
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {!showCalendly ? (
            <div className="p-6">
              <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Book Your Demo
                </h2>
                <p className="text-gray-600">
                  See Standing Encore in action with a personalized demo
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="John Smith"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="john@company.com"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Acme Corp"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                    disabled={loading}
                  />
                </div>
                
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                      loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }`}
                  >
                    {loading ? 'Submitting...' : 'Schedule Demo'}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
              
              <p className="mt-4 text-xs text-center text-gray-500">
                By submitting, you agree to our Privacy Policy and Terms of Service
              </p>
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-4 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h3>
                <p className="text-gray-600">
                  Please select a time that works best for you
                </p>
              </div>
              
              {/* Calendly Widget */}
              <div className="calendly-container" style={{ minHeight: '630px' }}>
                <InlineWidget
                  url="https://calendly.com/aipenguins/standing-encore"
                  prefill={{
                    name: formData.name,
                    email: formData.email,
                    customAnswers: {
                      a1: formData.company || ''
                    }
                  }}
                  pageSettings={{
                    hideEventTypeDetails: true,
                    hideLandingPageDetails: true,
                    hideGdprBanner: true
                  }}
                  styles={{
                    height: '630px',
                    minWidth: '320px'
                  }}
                />
              </div>
              
              <button
                onClick={handleClose}
                className="w-full mt-4 py-2 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoRequestModal;